import { type LangLocale } from '@myre/common-i18n';
import {
  DateFnsAdapter,
  LANGLOCALE,
  monthPickerFormatsFactory,
  TranslatePipe,
} from '@myre/ui-primitive-core';

import { NgClass } from '@angular/common';
import {
  Component,
  forwardRef,
  inject,
  Input,
  output,
  type OnInit,
} from '@angular/core';
import {
  FormsModule,
  NG_VALUE_ACCESSOR,
  type ControlValueAccessor,
} from '@angular/forms';
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
} from '@angular/material/core';
import {
  MatDatepicker,
  MatDatepickerInput,
} from '@angular/material/datepicker';
import { MatInput } from '@angular/material/input';

import { type DateTriggerIcon } from '../../questions/question-date';

export const DATE_PICKER_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => DatePickerComponent),
  multi: true,
};

@Component({
  selector: 'myre-date-picker',
  templateUrl: './date-picker.component.html',
  styleUrls: ['./date-picker.component.scss'],
  providers: [
    DATE_PICKER_VALUE_ACCESSOR,
    {
      provide: MAT_DATE_LOCALE,
      useValue: 'en-GB',
    },
    {
      provide: DateAdapter,
      useClass: DateFnsAdapter,
    },
    {
      provide: MAT_DATE_FORMATS,
      useFactory: monthPickerFormatsFactory,
      deps: [LANGLOCALE],
    },
  ],
  imports: [
    MatInput,
    MatDatepickerInput,
    FormsModule,
    NgClass,
    MatDatepicker,
    TranslatePipe,
  ],
})
export class DatePickerComponent implements OnInit, ControlValueAccessor {
  private _dateAdapter = inject<DateAdapter<any>>(DateAdapter);
  private readonly _langLocale = inject<LangLocale>(LANGLOCALE);

  @Input()
  public id: string;

  @Input()
  public required = false;

  @Input()
  public startAt: Date;

  @Input()
  public min: Date;

  @Input()
  public max: Date;

  @Input()
  public dateFilter: (date: Date) => boolean;

  @Input()
  public placeholder: string;

  @Input()
  public readonly = false;

  @Input()
  public error = false;

  @Input()
  public warning = false;

  @Input()
  public set triggerIcon(icon: DateTriggerIcon) {
    if (icon) {
      this._triggerIcon = icon;
    }
  }

  public get triggerIcon(): DateTriggerIcon {
    return this._triggerIcon || 'myre-icon-calendar';
  }

  public value: Date;
  public disabled = false;
  public focus = false;

  private _triggerIcon: DateTriggerIcon;

  public change = output<Date | null>();

  public ngOnInit(): void {
    this._dateAdapter.setLocale(this._langLocale);
  }

  public writeValue(value: Date): void {
    this.value = value;
  }

  public registerOnChange(fn: (value: Date) => any): void {
    this._onModelChange = fn;
  }

  public registerOnTouched(fn: () => any): void {
    this._onTouched = fn;
  }

  public setDisabledState(value: boolean): void {
    this.disabled = value;
  }

  public onChange(value: Date): void {
    this.value = value;
    this.change.emit(value);
    this._onModelChange(value);
  }

  public onBlur(event?: FocusEvent, icon?: any): void {
    if (event && event.relatedTarget && event.relatedTarget === icon) {
      return;
    }

    this.focus = false;
    this._onTouched();
  }

  public onFocus(): void {
    this.focus = true;
  }

  public onIconClick(picker: MatDatepicker<Date>): void {
    picker.open();
  }

  private _onModelChange: (value: Date) => any = () => {};

  private _onTouched: () => any = () => {};
}
